import type {MessageDescriptor, PrimitiveType} from 'react-intl';
import {messages} from '@smartmixin/i18n';
import {formatBearing} from '../Units';
import {MoonIllumination} from './astronomy';

/**
 * The small formatters the moon's view models share.
 *
 * A leaf, so that `nowView.ts` (the card) and `detailView.ts` (the page behind
 * it) can both use them without either importing the other. Three lines each,
 * but the card and the detail screen describe the same moon one tap apart, and
 * every time these have been written twice they have drifted — the illumination
 * line is FE #135, the compass form is FE #120.
 */

/**
 * The subset of `IntlShape` the moon's models need. `Units.formatBearing` and
 * `Dates.formatDuration` already spell theirs out inline for the same reason:
 * passing it in keeps `utils/Moon` free of react-intl, and passing it alongside
 * `dateTimeConfig` rather than reading `useDateTimeConfig()` keeps constate and
 * the Redux store out of a utils module.
 */
export type MoonIntl = {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
};

/** Denominator of "day 16.2 of 29.5". Formatted, not hardcoded: most locales
 *  write it 29,5. */
export const MEAN_LUNATION_DAYS = 29.5;

/**
 * "41°".
 *
 * `formatNumber`, never `toFixed`: the latter hardcodes the English decimal
 * separator into all 25 locales, which is FE #154.
 */
export const formatMoonDegrees = (value: number, intl: MoonIntl) =>
  `${intl.formatNumber(value, {maximumFractionDigits: 0})}°`;

/**
 * "E 99°".
 *
 * A space rather than the sun card's "ENE · 74°": the moon card's rail cells
 * are a third of a card wide and the settled frames print them without the
 * separator.
 *
 * The detail page's rows are full width and could have printed a bare number,
 * and the shipped screen deliberately does not: it describes the same rise and
 * the same set as the card, one tap away, so a compass point on one and a bare
 * number on the other is exactly the drift this seam exists to prevent. The
 * bare-degrees form survives one row down, on the field actually *labelled*
 * "Azimuth", where a letter would be redundant.
 *
 * The letters are localised per locale, never derived from the English table —
 * `units.ONO` is *Ost-Nord-Ost* in German and *Ouest-Nord-Ouest* in French,
 * i.e. opposite directions (FE #120). `Units.formatBearing` wraps at 360°, bins
 * the number it prints, and bidi-isolates the parts.
 */
export const formatMoonCompass = (bearing: number | null, intl: MoonIntl) =>
  formatBearing(bearing, intl, ' ') ?? '';

/** "364 708 km". Whole kilometres, grouped for the locale. */
export const formatMoonDistance = (km: number, intl: MoonIntl) =>
  `${intl.formatNumber(km, {maximumFractionDigits: 0})} km`;

/**
 * "32.75′". Two decimals, both bounds: the apparent diameter moves by about a
 * tenth of an arcminute a day, so one decimal would print the same number for
 * a week, and Intl would drop a trailing zero without the minimum.
 */
export const formatMoonApparentSize = (arcminutes: number, intl: MoonIntl) =>
  `${intl.formatNumber(arcminutes, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}′`;

/**
 * "94.2% lit · day 16.2 of 29.5".
 *
 * Takes the illumination rather than a timestamp, so the caller decides which
 * instant it describes — and both callers must decide the same way. The card
 * read the fraction off a `now`-keyed `getInfo` while the detail screen read it
 * off a midday one, and the two printed 94.2 % and 94.8 % for the same moon
 * (FE #135). Both now sample at local midday.
 */
export const formatIlluminationLine = (
  illumination: Pick<MoonIllumination, 'fraction' | 'age'>,
  intl: MoonIntl,
) =>
  `${intl.formatMessage(messages.astral.percentLit, {
    percent: intl.formatNumber(illumination.fraction, {
      style: 'percent',
      maximumFractionDigits: 1,
    }),
  })} · ${intl.formatMessage(messages.astral.dayOfCycle, {
    day: intl.formatNumber(illumination.age, {maximumFractionDigits: 1}),
    total: intl.formatNumber(MEAN_LUNATION_DAYS, {maximumFractionDigits: 1}),
  })}`;
